<template>
  <div class="card mb-4">
    <div class="card-content">
      <div class="content">
        <form @submit.prevent="checkout">
          <alert-message
            :messages="errorMessage"
            type="danger"
            ref="osvAlertMessage"
          />

          <!-- Title -->
          <div class="columns">
            <div class="column">
              <b-radio v-model="title" checked name="title" native-value="">
                {{ $t("titleList.undefined") }}
              </b-radio>
            </div>
            <div class="column">
              <b-radio v-model="title" name="title" native-value="mrs">
                {{ $t("titleList.mrs") }}
              </b-radio>
            </div>
            <div class="column">
              <b-radio v-model="title" name="title" native-value="mr">
                {{ $t("titleList.mr") }}
              </b-radio>
            </div>
          </div>

          <!-- Email -->
          <div class="form-group mb-5">
            <b-field
              :label="$t('email')"
              custom-class="required"
              label-position="on-border"
            >
              <b-input required v-model="email" type="email"></b-input>
            </b-field>
          </div>

          <!-- Lastname & Firstname -->
          <div class="columns mb-0">
            <div class="column pb-0">
              <b-field
                :label="$t('lastname')"
                custom-class="required"
                label-position="on-border"
              >
                <b-input required v-model="lastname"></b-input>
              </b-field>
            </div>
            <div class="column pb-0">
              <b-field
                :label="$t('firstname')"
                custom-class="required"
                label-position="on-border"
              >
                <b-input required v-model="firstname"></b-input>
              </b-field>
            </div>
          </div>

          <!-- Date of birth -->
          <div class="form-group">
            <date-of-birth v-model="dateOfBirth" />
          </div>

          <!-- Phone -->
          <div class="form-group">
            <b-field
                :label="$t('phone')"
                label-position="on-border"
                :custom-class="additionalRequiredFields.includes('phone') ? 'required' : ''"
            >
              <b-input
                v-model="phone"
                type="tel"
                v-cleave="masks.frenchPhone"
                @input.native="onPhoneNumberInput"
                @keydown.native="phoneNumberKeyDown"
                placeholder="06-00-00-00-00"
                maxlength="14"
                :has-counter="false"
                :required="additionalRequiredFields.includes('phone')"
              />
            </b-field>
          </div>

          <!-- Address -->
          <div class="form-group">
            <b-field
                :label="$t('address')"
                label-position="on-border"
                :custom-class="additionalRequiredFields.includes('address') ? 'required' : ''"
            >
              <b-input
                  v-model="address"
                  :required="additionalRequiredFields.includes('address')"
              />
            </b-field>
          </div>

          <!-- Postal Code & City -->
          <div class="form-group">
            <div class="columns">
              <div class="column">
                <b-field
                    :label="$t('postalCode')"
                    label-position="on-border"
                    :custom-class="additionalRequiredFields.includes('postalCode') ? 'required' : ''"
                >
                  <b-input
                      v-model="postalCode"
                      :required="additionalRequiredFields.includes('postalCode')"
                  />
                </b-field>
              </div>
              <div class="column">
                <b-field
                    :label="$t('city')"
                    label-position="on-border"
                    :custom-class="additionalRequiredFields.includes('city') ? 'required' : ''"
                >
                  <b-input
                      v-model="city"
                      :required="additionalRequiredFields.includes('city')"
                  />
                </b-field>
              </div>
            </div>
          </div>

          <!-- Checkbox list -->
          <div
              class="form-group"
              v-if="showNewsletter"
          >
            <b-checkbox v-model="newsletter">
              {{ $t("newsletter") }}
              <cite class="text-grey">{{ $t("newsletterPerMonth") }}</cite>
            </b-checkbox>
          </div>
          <div
              class="form-group"
              v-if="showCommercialOffer"
          >
            <b-checkbox v-model="commercialOffer">
              {{ $t("commercialOffer", { name: this.tenant }) }}
              <cite class="text-grey">{{ $t("promosAndOffers") }}</cite>
            </b-checkbox>
          </div>
          <div class="form-group">
            <b-checkbox v-model="acceptGdpr">
              {{ $t("acceptGdpr") }}
            </b-checkbox>
            <br />
            <a
              :href="osvConfig.paths.gdprCustomer"
              v-if="osvConfig.paths.gdprCustomer"
              target="_blank"
            >
              {{ $t("gdpr") }}
            </a>
          </div>

          <div class="form-group text-right">
            <a
              v-if="osvConfig.paths.signIn"
              :href="`${osvConfig.paths.signIn}?redirect_to=${thisLocation()}`"
            >
              <b-button
                type="is-link is-light"
                rounded
              >
                {{ $t("connection") }}
              </b-button>
            </a>
            <b-button
              type="is-primary"
              class="loadable-button"
              native-type="submit"
              rounded
            >
              {{ $t("checkout") }}
            </b-button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { HelpersMixin } from "@/mixins/HelpersMixin";
import { LocaleMixin } from "@/mixins/LocaleMixin";
import { CartMixin } from "@/mixins/CartMixin";
import { ConfigMixin } from "@/mixins/ConfigMixin";
import { PhoneNumberMixin } from "@/mixins/PhoneNumberMixin";
import VueI18n from "vue-i18n";
import AlertMessage from "../../../shared/AlertMessage";
import store from "@/store";
import cug from "@/services/cug";
import DateOfBirth from "@/components/ui/DateOfBirth";

export default {
  store,
  components: { AlertMessage, DateOfBirth },
  name: "CartCreateCustomer",
  mixins: [LocaleMixin, HelpersMixin, CartMixin, ConfigMixin, PhoneNumberMixin],

  props: {
    errorReport: Function,
    showCommercialOffer: {
      type: Boolean,
      default: true
    },
    showNewsletter: {
      type: Boolean,
      default: true
    },
    additionalRequiredFields: {
      type: Array,
      default: []
    }
  },

  data() {
    return {
      tenant: "",

      email: "",
      title: "",
      lastname: "",
      firstname: "",
      dateOfBirth: null,
      phone: "",
      address: "",
      city: "",
      postalCode: "",

      newsletter: false,
      commercialOffer: false,
      acceptGdpr: false,

      errorMessage: []
    };
  },

  beforeMount () {
    cug.whoami().then(rep => {
      this.tenant = rep.data.name;
    });
  },

  methods: {
    checkout () {
      const trackers = this.getAnalyticsTrackers();
      this.errorMessage = [];
      let data = {
        cart: {
          customer: {
            email: this.email,
            title: this.title,
            lastname: this.lastname,
            firstname: this.firstname,
            date_of_birth: this.dateOfBirth,
            phone: this.phone,
            address: this.address,
            city: this.city,
            zipcode: this.postalCode
          },
          email_subscribe: {
            newsletter: this.newsletter,
            commercial_offer: this.commercialOffer
          },
          trackers
        }
      };

      if (!this.acceptGdpr) {
        this.errorMessage = [
          this.$t("youMustAcceptGdpr", { name: this.tenant })
        ];
        return false;
      }

      this.loadButton(true);
      store
        .dispatch("cart/checkout", data)
        .then(resp => {
          if (resp.data.payment) {
            let paymentUrl = resp.data.payment.url;
            this.gotToPaymentPage(paymentUrl);
          } else {
            this.goToConfirmationPage(resp.data.purchase.reference);
          }
        })
        .catch(err => {
          this.errorReport(err);
        })
        .finally(() => {
          this.loadButton(false);
        });
    },

    thisLocation () {
      return document.location.pathname
    }
  },

  // Translate component
  mounted () {
    this.$i18n.locale = this.getLocale();
  },
  i18n: new VueI18n({
    locale: "fr",
    messages: {
      en: {
        connexionInformations: "My login details",
        myInformation: "My information",
        email: "Email",
        password: "Password",
        confirmPassword: "Confirm password",
        lastname: "Lastname",
        firstname: "Firstname",
        title: "Mister or Madame",
        dateOfBirth: "date of birth",
        datePlaceHolder: "dd/mm/yyyy",
        address: "Address",
        phone: "Phone",
        city: "City",
        postalCode: "Postal code",
        checkout: "Go to payement",
        connection: "Sign in",
        connexionError: "An error occurred during connection",
        titleList: {
          undefined: "Undefined",
          mr: "Mister",
          mrs: "Madame"
        },
        newsletter: "I would like to receive the newsletter",
        newsletterPerMonth: "(1 to 2 per month)",
        commercialOffer: "I would like to receive good maps of {name}",
        promosAndOffers: "(Promotions, personalized offers, etc.)",
        acceptGdpr:
          "I have read and agree to the processing of my personal data",
        gdpr: "Data Management Policy",
        youMustAcceptGdpr: "You must accept the {name}'s data management policy"
      },
      fr: {
        connexionInformations: "Mes informations de connexion",
        myInformation: "Mes informations",
        email: "Email",
        password: "Mot de passe",
        confirmPassword: "Confirmer le mot de passe",
        lastname: "Nom",
        firstname: "Prénom",
        title: "Monsieur ou Madame",
        dateOfBirth: "Date de naissance",
        datePlaceHolder: "jj/mm/aaaa",
        address: "Adresse",
        phone: "Téléphone",
        city: "Ville",
        postalCode: "Code postal",
        checkout: "Passer au paiement",
        connection: "Me connecter",
        connexionError: "Une erreur s'est produite lors de la connexion",
        titleList: {
          undefined: "Indéfini",
          mr: "Monsieur",
          mrs: "Madame"
        },
        newsletter: "Je souhaite recevoir la newsletter",
        newsletterPerMonth: "(1 à 2 par mois)",
        commercialOffer: "Je souhaite recevoir les bons plans de {name}",
        promosAndOffers: "(Promos, offres personnalisées, etc.)",
        acceptGdpr:
          "J'ai lu et j'accepte le traitement de mes données personnelles",
        gdpr: "Politique de gestion des données",
        youMustAcceptGdpr:
          "Vous devez accepter la politique de gestion des données de {name}"
      }
    }
  })
};
</script>

<style lang="scss" src="../../../../assets/styles/buefy/base.scss"></style>
